import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { MENU } from "@/data/content";
import { useCart } from "@/contexts/CartContext";

export default function MenuSection() {
  const [active, setActive] = useState(MENU[0].id);
  const current = MENU.find((m) => m.id === active);
  const { add, qtyOf, setOpen } = useCart();

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="bg-[var(--surface-light)] py-28 text-[var(--text-dark)] md:py-40"
    >
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-16 px-6 md:px-10 lg:grid-cols-12">
        {/* Sticky category rail */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <p className="mb-6 font-body text-xs uppercase tracking-[0.35em] text-accent">Order · Pickup</p>
            <h2 className="mb-6 font-display text-4xl leading-tight tracking-tight sm:text-5xl">
              Roasted with intent.
            </h2>
            <p className="mb-10 max-w-sm font-body text-sm leading-relaxed text-[var(--text-dark)]/60">
              Tap to add. Choose a pickup time and pay at the counter when you arrive — no card details needed.
            </p>
            <div className="flex flex-col gap-1">
              {MENU.map((cat) => (
                <button
                  key={cat.id}
                  data-testid={`menu-cat-${cat.id}`}
                  onClick={() => setActive(cat.id)}
                  className="group border-t border-black/10 py-5 text-left transition-colors duration-300"
                >
                  <span
                    className={`font-serif-c text-3xl transition-colors duration-300 ${
                      active === cat.id ? "text-accent" : "text-[var(--text-dark)]/40 group-hover:text-[var(--text-dark)]"
                    }`}
                  >
                    {cat.category}
                  </span>
                  <span className="mt-1 block font-body text-xs uppercase tracking-[0.2em] text-[var(--text-dark)]/40">
                    {cat.tagline}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
              data-testid={`menu-items-${active}`}
            >
              {current.items.map((item, i) => {
                const q = qtyOf(item.id);
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.5 }}
                    className="group flex items-center justify-between gap-6 border-b border-black/10 py-8"
                  >
                    <div className="min-w-0 max-w-xl">
                      <h3 className="font-serif-c text-2xl sm:text-3xl">{item.name}</h3>
                      <p className="mt-2 font-body text-sm leading-relaxed text-[var(--text-dark)]/55">{item.desc}</p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-4">
                      <span className="font-display text-2xl text-accent">${item.price}</span>
                      <button
                        type="button"
                        data-testid={`menu-add-${item.id}`}
                        onClick={() => { add(item); setOpen(true); }}
                        className={`relative flex h-11 min-w-11 items-center justify-center gap-2 rounded-full px-4 font-body text-xs uppercase tracking-[0.2em] transition-all duration-300 ${
                          q > 0
                            ? "bg-accent text-[var(--text)]"
                            : "border border-[var(--text-dark)]/20 text-[var(--text-dark)] hover:border-accent hover:text-accent"
                        }`}
                        aria-label={`Add ${item.name}`}
                      >
                        {q > 0 ? (
                          <>
                            <Check size={14} />
                            <span>In cart · {q}</span>
                          </>
                        ) : (
                          <>
                            <Plus size={16} />
                            <span className="hidden sm:inline">Add</span>
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
